import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1478720568477-152d9b164e26?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid min-h-screen bg-[#FAF9F6] lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#141416] lg:block">
        <img src={HERO} alt="Film projector" className="absolute inset-0 h-full w-full object-cover opacity-90" data-testid="login-hero-image" />
      </div>
      <div className="flex items-center px-6 py-16 sm:px-16">
        <div className="w-full max-w-sm animate-rise">
          <div className="mb-12"><Brand size="text-2xl" /></div>
          <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="auth-title">
            {mode === "login" ? "Welcome back." : "Start your shelf."}
          </h1>
          <p className="mt-3 text-sm text-zinc-500">{mode === "login" ? "Sign in to your video collection." : "Create an account to stream your Plex videos."}</p>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={form.name} onChange={set("name")} data-testid="auth-name-input" className="h-11 bg-white" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="auth-email-input" className="h-11 bg-white" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="auth-password-input" className="h-11 bg-white" />
            </div>
            {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="auth-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="auth-submit-button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-medium text-white transition-[background-color,transform] duration-150 hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
          <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} data-testid="auth-toggle-mode"
            className="mt-6 text-sm text-zinc-500 underline-offset-4 hover:text-zinc-900 hover:underline">
            {mode === "login" ? "No account? Create one" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
